/**
 * ErrorBoundary.tsx
 * 
 * React Error Boundary globale per catturare errori di rendering
 * e prevenire lo schermo bianco (white screen of death).
 * Gli errori vengono loggati e all'utente viene mostrata una UI di recovery.
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Copy } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary] Errore di rendering catturato:", error);
    console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleRecover = () => {
    this.setState({ hasError: false, error: null });
  };

  /**
   * Copia negli appunti il testo completo dell'errore (messaggio + stack +
   * URL della pagina + user agent). Pensato per essere incollato in chat
   * di assistenza, prompt AI o messaggi tra ruoli del portale.
   */
  private handleCopyError = async () => {
    const err = this.state.error;
    if (!err) return;
    const payload = [
      `Errore: ${err.message}`,
      `URL: ${typeof window !== "undefined" ? window.location.href : "n/d"}`,
      `User-Agent: ${typeof navigator !== "undefined" ? navigator.userAgent : "n/d"}`,
      `Quando: ${new Date().toISOString()}`,
      "",
      "Stack:",
      err.stack || "(non disponibile)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(payload);
      // Feedback minimale senza dipendere da sonner (potrebbe non essere
      // montato se il crash è avvenuto in alto nell'albero).
      // eslint-disable-next-line no-alert
      alert("Dettagli errore copiati negli appunti.");
    } catch {
      // eslint-disable-next-line no-alert
      alert("Copia non riuscita. Seleziona manualmente il testo dei dettagli.");
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center max-w-md space-y-4">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
            <h2 className="text-xl font-semibold text-foreground">
              Si è verificato un errore
            </h2>
            <p className="text-muted-foreground text-sm">
              Un errore imprevisto ha impedito il caricamento della pagina.
              Prova a ricaricare o torna alla pagina precedente.
            </p>
            {this.state.error && (
              <details className="text-left text-xs text-muted-foreground mt-2">
                <summary className="cursor-pointer">Dettagli tecnici</summary>
                {/* user-select abilitato esplicitamente perché alcuni layout
                    globali disabilitano la selezione testo; serve per
                    copiare manualmente se la Clipboard API non è disponibile. */}
                <pre
                  className="mt-1 p-2 bg-muted rounded text-[10px] overflow-auto max-h-40 select-text whitespace-pre-wrap break-all"
                  style={{ userSelect: "text" }}
                >
                  {this.state.error.message}
                  {"\n"}
                  {this.state.error.stack?.split("\n").slice(0, 8).join("\n")}
                </pre>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={this.handleCopyError}
                >
                  <Copy className="mr-2 h-3 w-3" />
                  Copia dettagli errore
                </Button>
              </details>
            )}
            <div className="flex gap-3 justify-center pt-2">
              <Button variant="outline" onClick={this.handleRecover}>
                Riprova
              </Button>
              <Button onClick={this.handleReload}>
                Ricarica pagina
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
